// ── BUDGET VS ACTUAL (Award Setup tab) ───────────────────────
// Import a month of actuals from the accounting system's GL export, map each
// GL account to an object class once, and see spend, variance and burn rate
// against the award's approved budget. Every figure is computed on the
// server (lib/actuals.js).
import { useState, useEffect, useCallback } from "react";
import { FONT_H, FONT_UI, FONT_MONO } from "../theme";
import { API, apiFetch } from "../utils/api";

const RED = "#dc2626", AMBER = "#d97706", GREEN = "#059669";
const money = (n) => (n == null ? "—" : `$${Math.round(Number(n)).toLocaleString()}`);
const FLAGS = {
  over:          { text: "Over budget",                    color: RED },
  "will-exceed": { text: "On pace to exceed",              color: AMBER },
  behind:        { text: "Behind a straight-line pace",    color: AMBER },
};

export default function BudgetVsActualTab({ T, card, label, input, btn, award }) {
  const [awards, setAwards]   = useState([]);
  const [awardId, setAwardId] = useState(award?.id || "");
  const [data, setData]       = useState(null);
  const [error, setError]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [period, setPeriod]   = useState(new Date(Date.now() - 20 * 86400000).toISOString().slice(0, 7));
  const [headers, setHeaders] = useState(null);
  const [cols, setCols]       = useState({ accountColumn: "", nameColumn: "", amountColumn: "" });
  const [draftMap, setDraftMap] = useState({});

  useEffect(() => {
    apiFetch(`${API}/setup/awards`).then((r) => r.json()).then((d) => {
      const list = d.records || [];
      setAwards(list);
      setAwardId((id) => id || list[0]?.id || "");
    }).catch(() => {});
  }, []);

  const show = (d) => { setData(d); setDraftMap({}); };
  const load = useCallback(async () => {
    if (!awardId) return;
    setError("");
    try {
      const res = await apiFetch(`${API}/actuals/${encodeURIComponent(awardId)}`);
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Could not load actuals.");
      show(d);
    } catch (e) { setError(e.message); setData(null); }
  }, [awardId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    setBusy(true); setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("period", period);
      Object.entries(cols).forEach(([k, v]) => v && fd.append(k, v));
      const res = await apiFetch(`${API}/actuals/${encodeURIComponent(awardId)}/import`, { method: "POST", body: fd });
      const d = await res.json();
      if (!res.ok) { if (d.headers) setHeaders(d.headers); throw new Error(d.error || "Could not import that file."); }
      setHeaders(null);
      show(d);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const call = async (url, method, body) => {
    setBusy(true); setError("");
    try {
      const res = await apiFetch(url, { method, headers: { "Content-Type": "application/json" }, body: body && JSON.stringify(body) });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "That did not work.");
      show(d);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  if (!awards.length) {
    return <div style={{ ...card, fontFamily: FONT_UI, fontSize: 13.5, color: T.textMuted }}>Save an award on the Notice of Award tab first. Its approved object classes are the budget lines the actuals are set against.</div>;
  }

  const r = data?.report;
  const th = { textAlign: "right", padding: "8px 10px", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textMuted, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const td = { textAlign: "right", padding: "9px 10px", fontFamily: FONT_UI, fontSize: 13, color: T.textBody, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" };
  const accounts = r ? [...r.unmapped.map((u) => u.account), ...Object.keys(data.mapping)] : [];

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={card}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <label style={{ minWidth: 260 }}><span style={label}>Award</span>
            <select style={input} value={awardId} onChange={(e) => setAwardId(e.target.value)}>
              {awards.map((a) => <option key={a.id} value={a.id}>{[a.awardNumber, a.projectTitle].filter(Boolean).join(" · ") || a.id}</option>)}
            </select>
          </label>
          <label><span style={label}>Month of these actuals</span>
            <input style={{ ...input, width: 170 }} type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
          <label style={{ ...btn(true), display: "inline-block", opacity: busy ? 0.6 : 1 }}>
            {busy ? "Working…" : "Upload GL export (.csv or .xlsx)"}
            <input type="file" accept=".csv,.xlsx" style={{ display: "none" }} disabled={busy}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }} />
          </label>
        </div>
        <p style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, margin: "10px 0 0" }}>
          Export the month's activity by GL account from QuickBooks, Sage Intacct, Xero or your trial balance. Uploading a month again replaces it.
        </p>
        {headers && (
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 12 }}>
            {[["accountColumn", "GL account column"], ["nameColumn", "Account name column (optional)"], ["amountColumn", "Amount column"]].map(([k, l]) => (
              <label key={k} style={{ minWidth: 200 }}><span style={label}>{l}</span>
                <select style={input} value={cols[k]} onChange={(e) => setCols({ ...cols, [k]: e.target.value })}>
                  <option value="">Choose…</option>
                  {headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
            <span style={{ alignSelf: "flex-end", fontFamily: FONT_UI, fontSize: 12, color: T.textMuted }}>Then upload the file again.</span>
          </div>
        )}
        {error && <div role="alert" style={{ marginTop: 12, color: RED, fontFamily: FONT_UI, fontSize: 13 }}>{error}</div>}
      </div>

      {r && (
        <>
          {!!r.warnings.length && (
            <div style={{ ...card, borderLeft: `3px solid ${AMBER}` }}>
              {r.warnings.map((w) => <p key={w} style={{ fontFamily: FONT_UI, fontSize: 13, color: T.textBody, margin: "4px 0", lineHeight: 1.5 }}>{w}</p>)}
            </div>
          )}

          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <div style={{ padding: "16px 20px 6px" }}>
              <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: 0 }}>Budget vs actual</h2>
              <p style={{ fontFamily: FONT_UI, fontSize: 12, color: T.textMuted, margin: "4px 0 0" }}>
                {r.period.monthsElapsed != null ? `Month ${r.period.monthsElapsed} of ${r.period.totalMonths}` : "Budget period not set"}
                {r.period.latestMonth && ` · actuals through ${r.period.latestMonth}`}
              </p>
            </div>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={{ ...th, textAlign: "left" }}>Object class</th>
                {["Budget", "Actual", "Remaining", "% spent", "Burn / month", "Projected", ""].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr></thead>
              <tbody>
                {r.lines.map((l) => (
                  <tr key={l.label}>
                    <td style={{ ...td, textAlign: "left", whiteSpace: "normal" }}>{l.label}</td>
                    <td style={td}>{money(l.budget)}</td>
                    <td style={td}>{money(l.actual)}</td>
                    <td style={{ ...td, color: l.variance < 0 ? RED : T.textBody }}>{money(l.variance)}</td>
                    <td style={td}>{l.pctSpent == null ? "—" : `${l.pctSpent}%`}</td>
                    <td style={td}>{money(l.burnPerMonth)}</td>
                    <td style={td}>{money(l.projected)}</td>
                    <td style={{ ...td, color: FLAGS[l.flag]?.color, fontWeight: 600 }}>{FLAGS[l.flag]?.text || ""}</td>
                  </tr>
                ))}
                <tr>
                  <td style={{ ...td, textAlign: "left", fontWeight: 700 }}>Total</td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(r.totals.budget)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(r.totals.actual)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{money(r.totals.variance)}</td>
                  <td style={{ ...td, fontWeight: 700 }}>{r.totals.pctSpent == null ? "—" : `${r.totals.pctSpent}%`}</td>
                  <td style={td} colSpan={3}>{r.unmappedTotal ? `${money(r.unmappedTotal)} not mapped yet` : ""}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {!!accounts.length && (
            <div style={card}>
              <h2 style={{ fontFamily: FONT_H, fontSize: 16, fontWeight: 700, color: T.text, margin: "0 0 4px" }}>GL accounts to budget lines</h2>
              <p style={{ fontFamily: FONT_UI, fontSize: 12.5, color: T.textMuted, margin: "0 0 12px" }}>Map each account once. Every later upload uses the same mapping.</p>
              {accounts.map((acct) => {
                const current = acct in draftMap ? draftMap[acct] : data.mapping[acct];
                const unmapped = r.unmapped.find((u) => u.account === acct);
                return (
                  <div key={acct} style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0", borderTop: `1px solid ${T.border}`, flexWrap: "wrap" }}>
                    <span style={{ minWidth: 220, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
                      <strong>{acct}</strong> {data.accountNames[acct] || ""}
                      {unmapped && <span style={{ color: AMBER, marginLeft: 6 }}>{money(unmapped.actual)} unmapped</span>}
                    </span>
                    <select style={{ ...input, maxWidth: 320 }} aria-label={`Budget line for GL account ${acct}`} value={current ?? "__none"}
                      onChange={(e) => setDraftMap({ ...draftMap, [acct]: e.target.value })}>
                      <option value="__none" disabled>Choose a budget line…</option>
                      {data.objectClasses.map((oc) => <option key={oc} value={oc}>{oc}</option>)}
                      <option value="">Not this award (leave out)</option>
                    </select>
                  </div>
                );
              })}
              <button style={{ ...btn(true), marginTop: 12 }} disabled={busy || !Object.keys(draftMap).length}
                onClick={() => call(`${API}/actuals/${encodeURIComponent(awardId)}/mapping`, "PUT", { mapping: draftMap })}>Save mapping</button>
            </div>
          )}

          {!!r.period.monthsImported.length && (
            <div style={card}>
              <span style={label}>Months imported</span>
              {r.period.monthsImported.map((m) => (
                <div key={m} style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0", borderTop: `1px solid ${T.border}`, fontFamily: FONT_UI, fontSize: 13, color: T.textBody }}>
                  <span style={{ minWidth: 80, fontFamily: FONT_MONO }}>{m}</span>
                  <span style={{ flex: 1, color: T.textMuted }}>{data.files[m]?.name}</span>
                  <button style={btn(false)} disabled={busy} onClick={() => window.confirm(`Remove the ${m} actuals?`) && call(`${API}/actuals/${encodeURIComponent(awardId)}/months/${m}`, "DELETE")}>Remove</button>
                </div>
              ))}
              <p style={{ fontFamily: FONT_UI, fontSize: 12, color: GREEN, margin: "8px 0 0" }}>{r.period.monthsImported.length} month(s) on record.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
